import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    NotificationsType,
    PaymentMethod,
    PaymentMethods,
    PaymentMethodsType,
    TypeNotificationType,
} from '@cargos/sprintpay-models';
import { Observable, Subject, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { RnplSloganComponent } from 'src/app/shared/components/rnpl-slogan/rnpl-slogan.component';
import { StripHtmlPipe } from 'src/app/shared/pipes/stripe-html.pipe';
import { SessionService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { PaymentMethodComplete } from 'src/app/shared/services/summary/models/types';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { LastFourDigitsComponent } from '../last-four-digits/last-four-digits.component';
import { CardTypeLibComponent } from '../logo-credit-card/card-type.component';
import { LogoPaymentMethodComponent } from '../logo-payment-method/logo-payment-method.component';

export interface PaymentMethodSelected {
    paymentMethodImage: string;
    paymentMethodValue: string;
    isCredit: boolean;
    paymentSelectedName?: PaymentMethodsType;
}

export enum StyleClassEnum {
    INFO = 'info',
    WARNING = 'warning',
    ERROR = 'error',
    SUCCESS = 'success',
}

@Component({
    standalone: true,
    selector: 'app-payment-method-selected',
    templateUrl: './payment-method-selected.component.html',
    styleUrls: ['./payment-method-selected.component.scss'],
    imports: [
        CommonModule,
        LastFourDigitsComponent,
        LogoPaymentMethodComponent,
        CardTypeLibComponent,
        MatIcon,
        MatTooltipModule,
        StripHtmlPipe,
        RnplSloganComponent,
    ],
    providers: [CurrencyPipe],
})
export class PaymentMethodSelectedComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public paymentSelectedName?: PaymentMethodsType;
    public paymentMethodsEnum = PaymentMethods;
    public paymentMethodImage = '';
    public paymentMethodValue = '';
    public isCredit: boolean = false;
    public isAuthenticated: boolean = false;
    public isEnoughCredit: boolean = false;
    public currentNotifications: NotificationsType[] = [];

    @Input() showValue = true;
    @Input() width = '80px';
    @Input() aboveThreshold: boolean = false;

    constructor(
        private sessionService: SessionService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private currencyPipe: CurrencyPipe,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.subscribeIsAllowedToPayWithCargoCredit();
        this.subscribePaymentMethodSelected();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribePaymentMethodSelected(): void {
        this.getPaymentMethodSelected$()
            .pipe(
                tap(({ paymentMethodImage, paymentMethodValue, isCredit, paymentSelectedName }) => {
                    this.paymentMethodImage = paymentMethodImage;
                    this.paymentMethodValue = paymentMethodValue;
                    this.isCredit = isCredit;
                    this.paymentSelectedName = paymentSelectedName;
                }),
                switchMap(() => {
                    if (!PaymentMethod.isCreditMethod(this.paymentSelectedName as PaymentMethodsType)) {
                        return of(null);
                    }

                    return this.featureRestrictionsService.isAllowedToPayWithCredit$(
                        this.paymentSelectedName,
                        this.aboveThreshold
                    );
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((isAllowedToPayWithCredit) => {
                this.isEnoughCredit = !!isAllowedToPayWithCredit?.isEnoughCredit;
                this.handleInsufficientCreditNotification();
            });
    }

    getPaymentMethodSelected$(): Observable<PaymentMethodSelected> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                this.isAuthenticated = isAuthenticated;
                if (isAuthenticated) {
                    return this.getAuthPaymentMethod$();
                }

                return this.getGuestPaymentMethod$();
            })
        );
    }

    getAuthPaymentMethod$(): Observable<PaymentMethodSelected> {
        return this.paymentMethodsHandlerService.getPaymentMethodSelectedComplete$(this.aboveThreshold).pipe(
            map((paymentSelected: PaymentMethodComplete | null) => {
                const paymentSelectedName = paymentSelected?.name;
                let paymentMethodImage: string = '';
                let paymentMethodValue: string = '';
                let isCredit: boolean = false;

                if (paymentSelected?.paymentMethodComplete instanceof PaymentMethod) {
                    if (
                        paymentSelected?.paymentMethodComplete instanceof PaymentMethod &&
                        paymentSelected?.name === PaymentMethods.PAYPAL
                    ) {
                        paymentMethodImage = PaymentMethods.PAYPAL;
                        isCredit = false;
                        paymentMethodValue = 'Paypal';
                    } else {
                        paymentMethodImage = (paymentSelected?.name as string) || '';
                        paymentMethodValue =
                            paymentSelected?.paymentMethodComplete.balance?.avaliableCredit?.toString() || '0';
                        isCredit = true;
                    }
                } else if (paymentSelected?.name === PaymentMethods.CREDIT_CARD) {
                    paymentMethodImage = paymentSelected?.paymentMethodComplete?.accountingDetails.cardType as string;
                    paymentMethodValue = paymentSelected?.paymentMethodComplete?.accountingDetails.lastFourDigits || '';
                    isCredit = false;
                } else if (paymentSelected?.name === PaymentMethods.ECHECK) {
                    paymentMethodImage = paymentSelected?.name as string;
                    paymentMethodValue =
                        paymentSelected?.paymentMethodComplete?.accountingDetails.accountLastFourDigits || '';
                    isCredit = false;
                }

                return {
                    paymentMethodImage,
                    paymentMethodValue,
                    isCredit,
                    paymentSelectedName,
                };
            })
        );
    }

    getGuestPaymentMethod$(): Observable<PaymentMethodSelected> {
        return this.paymentMethodsHandlerService.getPaymentMethodSelected$().pipe(
            switchMap((paymentMethodSelected) => {
                if (paymentMethodSelected?.method === PaymentMethods.CREDIT_CARD) {
                    return this.paymentMethodsHandlerService.getGuestCreditCard().pipe(
                        map((guestPaymentInformation) => {
                            const paymentSelectedName: PaymentMethodsType | undefined =
                                paymentMethodSelected?.method || undefined;
                            const paymentMethodImage: string =
                                guestPaymentInformation?.creditCardNonce?.details.cardType || '';
                            const paymentMethodValue: string =
                                guestPaymentInformation?.creditCardNonce?.details.lastFour || '';
                            const isCredit = false;

                            return {
                                paymentMethodImage,
                                paymentMethodValue,
                                isCredit,
                                paymentSelectedName,
                            } as PaymentMethodSelected;
                        })
                    );
                }

                const paymentSelectedName: PaymentMethodsType | undefined = paymentMethodSelected?.method || undefined;
                const paymentMethodImage: string = PaymentMethods.PAYPAL;

                return of({
                    paymentMethodImage,
                    paymentMethodValue: 'Paypal',
                    isCredit: false,
                    paymentSelectedName,
                } as PaymentMethodSelected);
            })
        );
    }

    subscribeIsAllowedToPayWithCargoCredit(): void {
        if (PaymentMethod.isCreditMethod(this.paymentSelectedName as PaymentMethodsType)) {
            this.featureRestrictionsService
                .isAllowedToPayWithCredit$(this.paymentSelectedName, this.aboveThreshold)
                .pipe(take(1))
                .subscribe((isAllowedToPayWithCargoCredit) => {
                    this.isEnoughCredit = !!isAllowedToPayWithCargoCredit.isEnoughCredit;
                });
        }
    }

    handleInsufficientCreditNotification(): void {
        if (this.isCredit && !this.isEnoughCredit && this.paymentSelectedName === this.paymentMethodsEnum.FINANCE) {
            const newNotifications = [
                ...this.currentNotifications,
                {
                    name: 'insufficient-credit',
                    alert: true,
                    type: TypeNotificationType.alert,
                    message: `<div><p class="fw-bolder">Insufficient Line of Credit</p><p class="m-0">${this.insufficientCreditWarning}</p></div>`,
                    display: true,
                    styleClass: StyleClassEnum.WARNING,
                },
            ];
        }
    }

    get invoicesInCartError(): string {
        let paymentMethod = 'SprintPay Net Terms';
        if (this.paymentSelectedName === this.paymentMethodsEnum.FINANCE) {
            paymentMethod = 'CargoSprint Credit';
        }
        return `<b> *${paymentMethod}</b> is unavailable when you are paying open invoices. Choose an alternate payment method to continue.`;
    }

    get insufficientCreditWarning(): string {
        const formatedCredit = this.currencyPipe.transform(this.paymentMethodValue) ?? '$0.00';
        return `Your Credit Available is <b>${formatedCredit}</b>, so this transaction cannot be processed. Please select a different payment method to proceed.`;
    }

    get isFinance(): boolean {
        return this.paymentSelectedName === this.paymentMethodsEnum.FINANCE;
    }

    get isSprintpayFlex(): boolean {
        return this.paymentSelectedName === this.paymentMethodsEnum.SPRINTPAY_FLEX;
    }
}
